import { useLocation, useParams } from 'react-router-dom';

import { Box } from '@cast/design-system';
import { useAbility } from '@cast/shared-ability-data-access';
import { RADAR_NAV_TITLE, useConsoleAccess } from '@cast/shared-trial-ui';
import {
  isKentEligibleCluster,
  isKentManagedCluster,
  isClusterManaged,
  useClusterQuery,
} from '@cast/shared-cluster-context-data-access';

import { useIsSecuritySunsetEnabled } from 'features/organization/security-insights/hooks/useIsSecuritySunsetEnabled';

import {
  BASE_PATHS,
  buildClusterAutoscalerPath,
  buildClusterCostReportBasePath,
  buildClusterPath,
  buildClusterSettingsPath,
  buildClusterStorageAutoscalerBasePath,
  buildClusterVulnerabilitiesPath,
  buildClusterWoopPath,
} from '@oneclick/constants/navigation';

import { canViewNavItem } from './canViewNavItem';
import {
  CLUSTER_AUTOMATION_SECTION,
  isHiddenUnderReportingLimit,
} from './reportingLimitedNav';
import { SidebarDivider } from './SidebarDivider';
import { SidebarItem } from './SidebarItem';
import { SidebarSection } from './SidebarSection';
import { SidebarTreeGroup } from './SidebarTreeGroup';
import {
  CLUSTER_SIDEBAR_SECTION_TITLES,
  clusterCostMonitoringChildren,
  clusterInventoryChildren,
  clusterNodeAutoscalerNavItem,
  clusterPodMutationsNavItem,
  clusterRebalancerNavItem,
  clusterBinPackingNavItem,
  clusterScoreNavItem,
  clusterSidebarIcons,
  clusterStorageAutoscalerChildren,
  clusterWoopChildren,
  optimizationConstraintsNavItem,
} from './sidebarNavConfig';
import { sidebarItemListSx } from './sidebarStyles';
import { useSidebarSectionExpanded } from './useSidebarSectionExpanded';
import { useWorkloadOptimizationAgentStatusQuery } from '@cast/split-me/hooks/queries/workload-optimization';
import { WorkloadOptimizationAgentStatus } from '@cast/types';

export const ClusterLevelSidebar = () => {
  const { clusterId } = useParams<{ clusterId: string }>();
  const { pathname } = useLocation();
  const {
    limitAutomationConsole,
    showAutomationOverviewNav,
    showAutomationUpsell,
  } = useConsoleAccess();
  const hideReportingInsights = limitAutomationConsole && !showAutomationUpsell;

  const { cluster } = useClusterQuery(clusterId);
  const isSecuritySunsetEnabled = useIsSecuritySunsetEnabled();
  const isKentEligible = !!cluster && isKentEligibleCluster(cluster);
  const isKentManaged = !!cluster && isKentManagedCluster(cluster);
  const isManaged = isClusterManaged(cluster);
  const isPhase2Cluster = isManaged || isKentManaged;

  const dashboardPath = buildClusterPath(clusterId!);
  const isDashboardActive =
    pathname === dashboardPath || pathname === `${dashboardPath}/`;

  const costReportBasePath = buildClusterCostReportBasePath(clusterId!);
  const clusterBasePath = buildClusterPath(clusterId!);
  const isCostMonitoringActive = clusterCostMonitoringChildren.some((child) =>
    child.isActive(pathname, clusterBasePath, costReportBasePath)
  );

  const { expanded: costMonitoringExpanded, onToggle: onToggleCostMonitoring } =
    useSidebarSectionExpanded(isCostMonitoringActive, pathname);

  const ability = useAbility();
  const clusterConditions = { clusterId };

  const canViewCostMonitoring = canViewNavItem(
    ability,
    [['view', 'ClusterCostMonitoring']],
    clusterConditions
  );

  const visibleCostMonitoringChildren = canViewCostMonitoring
    ? clusterCostMonitoringChildren.filter((child) => {
        if (isHiddenUnderReportingLimit(hideReportingInsights, child)) {
          return false;
        }
        if (isKentEligible && child.id === 'availableSavings') {
          return false;
        }
        return canViewNavItem(ability, child.rules, clusterConditions);
      })
    : [];

  const [
    canViewStopPersistentVolumes,
    canViewStopPvPolicies,
    canViewStopConfig,
    canViewStopEvents,
  ] = ability.canMany(
    [
      ['view', 'ClusterStopPersistentVolumes'],
      ['view', 'ClusterStopPvPolicies'],
      ['view', 'ClusterStopConfig'],
      ['view', 'ClusterStopEvents'],
    ],
    clusterConditions
  );
  const canViewClusterLevelStop = ability.canOneOf(
    'view',
    [
      'ClusterStopPersistentVolumes',
      'ClusterStopPvPolicies',
      'ClusterStopConfig',
      'ClusterStopEvents',
    ],
    clusterConditions
  );
  const canViewVulnerabilities = ability.can(
    'view',
    'OrganizationSecurityVulnerabilities'
  );
  const canViewClusterSettings = canViewNavItem(
    ability,
    [['view', 'ClusterAutoscaler']],
    clusterConditions
  );
  const canViewAuditLog = canViewNavItem(
    ability,
    [['view', 'ClusterAuditLog']],
    clusterConditions
  );
  const canViewClusterScore =
    !isHiddenUnderReportingLimit(hideReportingInsights, clusterScoreNavItem) &&
    canViewNavItem(ability, clusterScoreNavItem.rules, clusterConditions);
  const canViewOptimizationConstraints = canViewNavItem(
    ability,
    optimizationConstraintsNavItem.rules,
    clusterConditions
  );
  const canViewRebalancer = canViewNavItem(
    ability,
    clusterRebalancerNavItem.rules,
    clusterConditions
  );
  const canViewPodMutations = canViewNavItem(
    ability,
    clusterPodMutationsNavItem.rules,
    clusterConditions
  );
  const canViewBinPacking = canViewNavItem(
    ability,
    clusterBinPackingNavItem.rules,
    clusterConditions
  );

  const autoscalerPath = buildClusterAutoscalerPath(clusterId!);
  const woopBasePath = buildClusterWoopPath(clusterId!);
  const storageAutoscalerBasePath = buildClusterStorageAutoscalerBasePath(
    clusterId!
  );
  const settingsPath = buildClusterSettingsPath(clusterId!);
  const vulnerabilitiesPath = buildClusterVulnerabilitiesPath(clusterId!);
  const rebalancerPath = clusterRebalancerNavItem.buildPath(clusterBasePath);
  const podMutationsPath = clusterPodMutationsNavItem.buildPath(settingsPath);
  const binPackingPath = clusterBinPackingNavItem.buildPath(clusterBasePath);
  const clusterScorePath = `${clusterBasePath}/${clusterScoreNavItem.segment}`;
  const optimizationConstraintsPath = `${clusterBasePath}/${optimizationConstraintsNavItem.segment}`;

  const { data: woopAgentDetails } = useWorkloadOptimizationAgentStatusQuery({
    clusterId: clusterId!,
    enabled: !!clusterId,
  });
  const isWoopInstalled =
    woopAgentDetails?.status === WorkloadOptimizationAgentStatus.RUNNING;

  const isRebalancerEnabled = isManaged || isKentEligible;
  const isPodMutationsEnabled = isPhase2Cluster;
  const isBinPackingEnabled = isPhase2Cluster || isWoopInstalled;

  const visibleInventoryChildren = clusterInventoryChildren.filter(
    (child) =>
      !isHiddenUnderReportingLimit(hideReportingInsights, child) &&
      canViewNavItem(ability, child.rules, clusterConditions)
  );

  const visibleWoopChildren = clusterWoopChildren.filter((child) =>
    canViewNavItem(ability, child.rules, clusterConditions)
  );

  const canViewNodeAutoscaler = canViewNavItem(
    ability,
    isKentEligible
      ? clusterNodeAutoscalerNavItem.kentRules
      : clusterNodeAutoscalerNavItem.rules,
    clusterConditions
  );

  const visibleStorageAutoscalerChildren =
    clusterStorageAutoscalerChildren.filter((child) => {
      switch (child.id) {
        case 'persistentVolumes':
          return canViewStopPersistentVolumes;
        case 'pvPolicies':
          return canViewStopPvPolicies;
        case 'events':
          return canViewStopEvents;
        case 'storageConfiguration':
          return canViewStopConfig;
        default:
          return false;
      }
    });

  const isAnyWoopChildActive = pathname.startsWith(woopBasePath);
  const { expanded: isWoopExpanded, onToggle: onToggleWoop } =
    useSidebarSectionExpanded(isAnyWoopChildActive, pathname);
  const isAnyStorageAutoscalerChildActive = pathname.startsWith(
    storageAutoscalerBasePath
  );
  const {
    expanded: isStorageAutoscalerExpanded,
    onToggle: onToggleStorageAutoscaler,
  } = useSidebarSectionExpanded(isAnyStorageAutoscalerChildActive, pathname);

  const firstCostMonitoringChild = visibleCostMonitoringChildren[0];
  const firstCostMonitoringPath = firstCostMonitoringChild
    ? firstCostMonitoringChild.buildPath(clusterBasePath, costReportBasePath)
    : undefined;

  const firstWoopChild = visibleWoopChildren[0];
  const firstWoopPath = firstWoopChild
    ? `${woopBasePath}/${firstWoopChild.segment}`
    : undefined;

  const nodeAutoscalerLinkPath = `${autoscalerPath}/settings`;
  const isNodeAutoscalerActive = pathname.startsWith(autoscalerPath);

  const firstStorageAutoscalerChild = visibleStorageAutoscalerChildren[0];
  const firstStorageAutoscalerPath = firstStorageAutoscalerChild
    ? `${storageAutoscalerBasePath}/${firstStorageAutoscalerChild.segment}`
    : undefined;

  const isClusterScoreActive = clusterScoreNavItem.isPrefix
    ? pathname.startsWith(clusterScorePath)
    : pathname === clusterScorePath || pathname === `${clusterScorePath}/`;

  return (
    <>
      <SidebarItem
        title="Back to all clusters"
        to={BASE_PATHS.clusters}
        icon={clusterSidebarIcons.back}
      />

      <SidebarDivider />

      <SidebarSection>{CLUSTER_SIDEBAR_SECTION_TITLES.cluster}</SidebarSection>

      <Box sx={sidebarItemListSx}>
        <SidebarItem
          title="Dashboard"
          to={dashboardPath}
          active={isDashboardActive}
          icon={clusterSidebarIcons.dashboard}
        />

        {isSecuritySunsetEnabled &&
        canViewVulnerabilities &&
        !limitAutomationConsole ? (
          <SidebarItem
            title="Vulnerabilities"
            to={vulnerabilitiesPath}
            active={pathname.startsWith(vulnerabilitiesPath)}
            icon={clusterSidebarIcons.vulnerabilities}
          />
        ) : null}

        {canViewClusterScore ? (
          <SidebarItem
            title={clusterScoreNavItem.title}
            to={clusterScorePath}
            active={isClusterScoreActive}
            icon={clusterSidebarIcons.clusterScore}
          />
        ) : null}

        {visibleCostMonitoringChildren.length > 0 ? (
          <>
            <SidebarItem
              title={RADAR_NAV_TITLE}
              icon={clusterSidebarIcons.costReport}
              to={firstCostMonitoringPath}
              expanded={costMonitoringExpanded}
              indicated={isCostMonitoringActive}
              showCaret
              onToggle={onToggleCostMonitoring}
            />

            {costMonitoringExpanded ? (
              <SidebarTreeGroup>
                {visibleCostMonitoringChildren.map((child) => {
                  const linkPath = child.buildPath(
                    clusterBasePath,
                    costReportBasePath
                  );
                  return (
                    <SidebarItem
                      key={child.id}
                      title={child.title}
                      to={linkPath}
                      active={child.isActive(
                        pathname,
                        clusterBasePath,
                        costReportBasePath
                      )}
                      icon={clusterSidebarIcons[child.id]}
                      isNested
                    />
                  );
                })}
              </SidebarTreeGroup>
            ) : null}
          </>
        ) : null}
      </Box>

      <SidebarDivider />

      <SidebarSection>
        {CLUSTER_SIDEBAR_SECTION_TITLES.inventory}
      </SidebarSection>

      <Box sx={sidebarItemListSx}>
        {visibleInventoryChildren.map((child) => {
          const linkPath = child.buildPath(clusterBasePath, costReportBasePath);
          return (
            <SidebarItem
              key={child.id}
              title={child.title}
              to={linkPath}
              active={child.isActive(
                pathname,
                clusterBasePath,
                costReportBasePath
              )}
              icon={clusterSidebarIcons[child.icon]}
            />
          );
        })}
      </Box>

      {!(
        limitAutomationConsole &&
        CLUSTER_AUTOMATION_SECTION.hiddenWhenReportingLimited
      ) ? (
        <>
          <SidebarDivider />

          <SidebarSection>
            {CLUSTER_SIDEBAR_SECTION_TITLES.automation}
          </SidebarSection>

          <Box sx={sidebarItemListSx}>
            {canViewClusterSettings ? (
              <SidebarItem
                title="Overview"
                to={settingsPath}
                active={
                  pathname === settingsPath || pathname === `${settingsPath}/`
                }
                icon={clusterSidebarIcons.overview}
              />
            ) : null}
            {canViewOptimizationConstraints ? (
              <SidebarItem
                title={optimizationConstraintsNavItem.title}
                to={optimizationConstraintsPath}
                active={
                  pathname === optimizationConstraintsPath ||
                  pathname === `${optimizationConstraintsPath}/`
                }
                icon={clusterSidebarIcons.optimizationConstraints}
              />
            ) : null}
            {visibleWoopChildren.length > 0 ? (
              <>
                <SidebarItem
                  title="Workload autoscaler"
                  icon={clusterSidebarIcons.workloadAutoscaler}
                  to={firstWoopPath}
                  expanded={isWoopExpanded}
                  indicated={isAnyWoopChildActive}
                  showCaret
                  onToggle={onToggleWoop}
                />
                {isWoopExpanded ? (
                  <SidebarTreeGroup>
                    {visibleWoopChildren.map((child) => {
                      const linkPath = `${woopBasePath}/${child.segment}`;
                      return (
                        <SidebarItem
                          key={child.id}
                          title={child.title}
                          to={linkPath}
                          active={pathname.startsWith(linkPath)}
                          isNested
                        />
                      );
                    })}
                  </SidebarTreeGroup>
                ) : null}
              </>
            ) : null}
            {canViewNodeAutoscaler ? (
              <SidebarItem
                title={clusterNodeAutoscalerNavItem.title}
                icon={clusterSidebarIcons.nodeAutoscaler}
                to={nodeAutoscalerLinkPath}
                active={isNodeAutoscalerActive}
              />
            ) : null}
            {canViewClusterLevelStop &&
            visibleStorageAutoscalerChildren.length > 0 ? (
              <>
                <SidebarItem
                  title="Storage autoscaler"
                  icon={clusterSidebarIcons.storageAutoscaler}
                  to={firstStorageAutoscalerPath}
                  expanded={isStorageAutoscalerExpanded}
                  indicated={isAnyStorageAutoscalerChildActive}
                  showCaret
                  onToggle={onToggleStorageAutoscaler}
                />
                {isStorageAutoscalerExpanded ? (
                  <SidebarTreeGroup>
                    {visibleStorageAutoscalerChildren.map((child) => {
                      const linkPath = `${storageAutoscalerBasePath}/${child.segment}`;
                      return (
                        <SidebarItem
                          key={child.id}
                          title={child.title}
                          to={linkPath}
                          active={pathname.startsWith(linkPath)}
                          isNested
                        />
                      );
                    })}
                  </SidebarTreeGroup>
                ) : null}
              </>
            ) : null}
            {canViewBinPacking ? (
              <SidebarItem
                title={clusterBinPackingNavItem.title}
                to={binPackingPath}
                active={pathname.startsWith(binPackingPath)}
                icon={clusterSidebarIcons.evictor}
                disabledTooltip={`Enable automation to use ${clusterBinPackingNavItem.title}`}
                disabled={!isBinPackingEnabled}
              />
            ) : null}
            {canViewRebalancer ? (
              <SidebarItem
                title={clusterRebalancerNavItem.title}
                to={rebalancerPath}
                active={pathname.startsWith(rebalancerPath)}
                icon={clusterSidebarIcons.rebalancer}
                disabled={!isRebalancerEnabled}
                disabledTooltip={`Enable automation to use ${clusterRebalancerNavItem.title}`}
              />
            ) : null}
            {canViewPodMutations ? (
              <SidebarItem
                title={clusterPodMutationsNavItem.title}
                to={podMutationsPath}
                active={pathname.startsWith(podMutationsPath)}
                icon={clusterSidebarIcons.podMutations}
                disabled={!isPodMutationsEnabled}
                disabledTooltip={`Enable automation to use ${clusterPodMutationsNavItem.title}`}
              />
            ) : null}
            {canViewAuditLog ? (
              <SidebarItem
                title="Logs"
                to={`${clusterBasePath}/logs`}
                active={pathname.startsWith(`${clusterBasePath}/logs`)}
                icon={clusterSidebarIcons.logs}
              />
            ) : null}
          </Box>
        </>
      ) : showAutomationOverviewNav && canViewClusterSettings ? (
        <>
          <SidebarDivider />
          <SidebarSection>
            {CLUSTER_SIDEBAR_SECTION_TITLES.automation}
          </SidebarSection>
          <Box sx={sidebarItemListSx}>
            <SidebarItem
              title="Overview"
              to={settingsPath}
              active={
                pathname === settingsPath || pathname === `${settingsPath}/`
              }
              icon={clusterSidebarIcons.overview}
            />
          </Box>
        </>
      ) : null}
    </>
  );
};
